/**
 * SSE 解析核心 —— 契约 §3.3 事件序列 + Lead Directives §5.6。
 *
 * 这是全项目最易出 bug 的模块，因为它同时被三种完全不同的传输喂养：
 *   - Web  端：`fetch` + `ReadableStream`，chunk 边界任意
 *   - 小程序：`uni.request({ enableChunked: true })` 的 `onChunkReceived`，
 *             chunk 会按 ArrayBuffer 切片，断裂点比 fetch 更碎
 *   - 服务端：自己的上游 OpenAI 兼容流
 * 行为漂移的表现是"Web 端正常、小程序端偶发丢字"，极难复现。
 * 因此解析器只写一份放在 contracts，三端共用同一实现。
 *
 * 兼容范围：WHATWG SSE 规范的行终止符（`\n` / `\r\n` / `\r`）、
 * 多行 `data:` 拼接、注释行（`:` 开头）、`[DONE]` 哨兵、跨 chunk 半包。
 *
 * 边界说明：解析器**只做解析与派发，不做文本聚合**。
 * `delta` 的拼接由调用方负责（前端要按帧节流渲染，后端要按 index 落库，
 * 两端聚合策略不同），本模块导出 `joinDeltaContents()` 作为两端共用的实现。
 */

import { z } from 'zod';
import { AgeBandSchema, ChatFinishReasonSchema, DomainSchema, QuotaSchema } from './dto.js';
import { ErrorCode } from './errors.js';
import type { AgeBand, Domain } from './domain.js';
import type { ChatFinishReason, Quota } from './dto.js';
import type { SafetyVerdict } from './dto.js';

/* ------------------------------------------------------------------ *
 * 事件名与 payload 形状（Tech Lead 冻结签名）
 * ------------------------------------------------------------------ */

/**
 * 契约 §3.3：`meta` 必须首帧、`done` 必须末帧、`error` 可出现在任意位置。
 *
 * `retract`（v1.1.0 新增）是**输出侧安全判定**的撤回帧：回答已经完整下发，
 * 但事后判定为不可用，前端必须把这条回答从界面上撤掉。它排在 `usage` 与 `done` 之间 ——
 * 「整条撤回」不是错误，所以流照常以 `done` 收尾；被撤回的回答不消耗额度，因此不发 `usage`。
 */
export const SSE_EVENT_NAMES = ['meta', 'delta', 'usage', 'retract', 'done', 'error'] as const;
export type SseEventName = (typeof SSE_EVENT_NAMES)[number];

export function isSseEventName(value: unknown): value is SseEventName {
  return typeof value === 'string' && (SSE_EVENT_NAMES as readonly string[]).includes(value);
}

/** 首帧：前端据此立即上屏用户气泡 + 占位回答 */
export interface SseMetaPayload {
  requestId: string;
  conversationId: string;
  userMessageId: string;
  assistantMessageId: string;
  domain: Domain;
  ageBand: AgeBand | null;
  routeConfidence: number;
  upstreamModel: string | null;
}

/** 增量文本：`content` 是纯增量，前端 `+=` 拼接 */
export interface SseDeltaPayload {
  index: number;
  content: string;
}

/**
 * 计量与额度快照。
 *
 * `estimated` 是**必填**字段，依据有两处：
 * ① 契约 §3.3 的 usage 帧字段表（v1.0.0 补记）；
 * ② Lead Directives §5.2 —— "上游未返回 usage 时用估算器并在 Message 上标 estimated: true"。
 * 前端需要该信号来给估算值加"约"字样；若省略，前端无法区分精确值与估算值。
 *
 * ★ v1.3.0 起**删掉** `promptTokens / completionTokens / totalTokens`：单轮消耗的绝对
 * token 量是服务端对账信息，不下发客户端（用户裁决 2026-10-05）。前端要的"额度还剩
 * 多少"由 `quota` 的百分比回答；要的"这一轮是不是估算的"由 `estimated` 回答。
 * 两个需求都不需要 token 数，于是它们没有留在帧里的理由。
 */
export interface SseUsagePayload {
  estimated: boolean;
  quota: Quota;
}

export interface SseDonePayload {
  messageId: string;
  finishReason: ChatFinishReason;
  latencyMs: number;
}

export interface SseErrorPayload {
  code: number;
  message: string;
  safety?: SafetyVerdict;
}

/**
 * `retract` 帧：回答已经完整下发，但**事后**判定为不可用，整条撤回（v1.1.0 新增）。
 *
 * 为什么不是 `error`：这不是"本轮失败"。用户拿到了一条完整、语法通顺的回答，
 * 只是它不能留在会话里（安全判定不通过）。用 `error` 表达会带来两个错误后果：
 * ① 前端的既有语义是"error 出现即终结"，此后不能有 `done`；
 * ② 现有前端会保留已渲染的文本并追加提示条（用户希望的是**整条消失**）。
 *
 * 字段说明：
 * - `messageId`：被撤回的回答 id，与 `meta.assistantMessageId` 一致。前端据此定位要撤下的气泡；
 *   服务端也已用同一个 id 落了一条**占位消息**（正文为 `safety.publicMessage`），
 *   因此刷新后仍能看到"这条回答被撤回"的痕迹，而不是凭空少一轮对话。
 * - `reason`：内部技术理由（可能含规则 id），**不要展示给用户**；展示话术用 `safety.publicMessage`。
 * - `safety`：六字段全必填，与 `error.safety` 同形。
 */
export interface SseRetractPayload {
  messageId: string;
  reason: string;
  safety: SafetyVerdict;
}

/**
 * 事件名 → payload 的映射。
 *
 * 用 type alias 而非 `interface`：接口的泛型实例化不分发成联合类型，
 * 这里保持一致形状，避免下游用 `SseEventMap[K]` 做条件类型时行为不一致。
 */
export type SseEventMap = {
  meta: SseMetaPayload;
  delta: SseDeltaPayload;
  usage: SseUsagePayload;
  retract: SseRetractPayload;
  done: SseDonePayload;
  error: SseErrorPayload;
};

/**
 * 一个已解析的事件。
 *
 * 注意：`SseEvent`（K 取默认值 `SseEventName`）**不是**判别联合——
 * `SseEventMap[SseEventName]` 会先求值成六个 payload 的联合，
 * 于是 `data` 是联合而不是"随 event 变化的对应类型"。
 * 想要收窄请用导出的 `isSseEventName` 守卫：
 * `if (isSseEventName(evt.event) && evt.event === 'delta') { ... }`
 * 或直接 `evt.event === 'delta'` 后取 `evt.data` 再用 payload schema 校验。
 * 三端消费方因此不必写 `as` 断言（规范禁止 `as any`）。
 */
export interface SseEvent<K extends SseEventName = SseEventName> {
  event: K;
  data: SseEventMap[K];
  raw: string;
}

/* ------------------------------------------------------------------ *
 * zod schema（对 payload 做运行时校验，校验失败同样走 onParseError）
 * ------------------------------------------------------------------ */

export const SseMetaPayloadSchema = z.object({
  requestId: z.string().min(1),
  conversationId: z.string().min(1),
  userMessageId: z.string().min(1),
  assistantMessageId: z.string().min(1),
  domain: DomainSchema,
  ageBand: AgeBandSchema.nullable(),
  routeConfidence: z.number().min(0).max(1),
  upstreamModel: z.string().nullable(),
});

export const SseDeltaPayloadSchema = z.object({
  index: z.number().int().min(0),
  content: z.string(),
});

export const SseUsagePayloadSchema = z.object({
  // v1.3.0：三个 token 字段已删除（见上方 SseUsagePayload 的文档）。
  estimated: z.boolean(),
  quota: QuotaSchema,
});

export const SseDonePayloadSchema = z.object({
  messageId: z.string().min(1),
  // `finishReason` 是本服务的**受控词汇表**，不得出现上游厂商词汇。
  // 上游返回 `content_filter` 时，由服务端映射为 `safety_blocked` 并记日志——
  // 若让上游枚举直接穿透到 done 帧，前端的判断逻辑就被绑死在某个供应商的命名上，
  // 这个耦合将来无法移除。受控词汇表是对外契约的基本卫生。
  finishReason: z.enum(['stop', 'length', 'aborted', 'safety_blocked']),
  latencyMs: z.number().int().min(0),
});

/**
 * 安全判定的对外形状（契约 §4 的 SafetyVerdict），**六个字段全部必填**。
 *
 * `error` 帧与 `retract` 帧共用它，因此名字里不带事件名。
 *
 * 不做 `.partial()` 放宽：一个"字段在场但每个属性都说不清"的 verdict 对前端是
 * 不可渲染的半成品——`action` 决定走哪条展示分支、`publicMessage` 是唯一可直接
 * 展示的话术、`riskLevel` 决定告警级别，缺任何一个都没法用。
 * "上游故障类 error 帧没有安全判定"这个场景由 **`safety` 字段本身可选** 表达：
 * 字段缺席即精确表示"没有安全判定"，无需再叠一层残缺。
 */
export const SseSafetyVerdictSchema = z.object({
  action: z.enum(['allow', 'block', 'redirect', 'flag']),
  riskLevel: z.enum(['none', 'low', 'medium', 'high', 'critical']),
  categories: z.array(z.string()),
  reason: z.string(),
  publicMessage: z.string(),
  redirectTo: z.string().nullable(),
});

/** 旧名保留：`error` 帧的 safety 与 `retract` 帧的 safety 是同一形状。 */
export const SseErrorSafetySchema = SseSafetyVerdictSchema;

export const SseErrorPayloadSchema = z.object({
  code: z.number().int(),
  message: z.string(),
  safety: SseSafetyVerdictSchema.optional(),
});

export const SseRetractPayloadSchema = z.object({
  messageId: z.string().min(1),
  reason: z.string(),
  safety: SseSafetyVerdictSchema,
});

/** 事件名 → payload schema，解析器内部按事件名选表校验 */
export const SSE_PAYLOAD_SCHEMAS = {
  meta: SseMetaPayloadSchema,
  delta: SseDeltaPayloadSchema,
  usage: SseUsagePayloadSchema,
  retract: SseRetractPayloadSchema,
  done: SseDonePayloadSchema,
  error: SseErrorPayloadSchema,
} as const;

/** OpenAI 兼容流的结束哨兵，必须在解析层丢弃，绝不回调给业务 */
export const SSE_DONE_SENTINEL = '[DONE]';

/** 上游故障类错误码：不消耗额度（契约 §0.2 前端处置列） */
export const NON_BILLABLE_ERROR_CODES: readonly number[] = [
  ErrorCode.UPSTREAM_ERROR,
  ErrorCode.UPSTREAM_TIMEOUT,
];

/* ------------------------------------------------------------------ *
 * 解析器
 * ------------------------------------------------------------------ */

/** 解析失败（JSON 坏帧 或 payload 校验不过）时的回调载荷 */
export interface SseParseErrorInfo {
  /** 引发失败的 `data:` 原文（多行已按规范用 `\n` 拼接） */
  raw: string;
  /** 同一事件块内声明的 event 名；无声明时为空字符串 */
  event: string;
  /** 人类可读的失败原因（JSON 语法错误信息 或 zod issue 摘要） */
  reason: string;
}

export interface SseParserOptions {
  onEvent: (evt: SseEvent) => void;
  onParseError?: (err: SseParseErrorInfo) => void;
}

export interface SseParser {
  /** 喂入一个 chunk 的已解码文本片段。内部保留半包缓冲 */
  push(chunk: string): void;
  /** 流结束时调用：派发缓冲区里最后一个未以空行收尾的事件 */
  flush(): void;
  /** 复位：清空缓冲与未完成事件块，用于同一条连接的复用或重连重试 */
  reset(): void;
}

/**
 * 创建一个增量 SSE 解析器。
 *
 * 关键不变量：
 * - 只在事件边界（空行）派发；半包一律留在内部 buffer，调用方无需关心 chunk 切分。
 * - buffer 里永远只保留"最后一行不完整的部分"，不会随流无限增长。
 * - 行终止符在**找到时**就被消费掉：遇到 `\r` 时向后看一格，是 `\n` 就一起吃，
 *   否则单独消费 `\r`。这样既支持 CRLF，也支持老式 Mac 的裸 CR，
 *   且不需要跨 chunk 保留"待定 CR"状态（那是最容易漏掉的边界）。
 */
export function createSseParser(options: SseParserOptions): SseParser {
  const { onEvent, onParseError } = options;

  let buffer = '';
  let streamOffset = 0;

  /** 当前事件块的累积字段 */
  let eventName: string | null = null;
  const dataLines: string[] = [];
  let dataStartOffset = -1;
  let sawField = false;
  let bomStripped = false;

  function resetEventBlock(): void {
    eventName = null;
    dataLines.length = 0;
    dataStartOffset = -1;
    sawField = false;
  }

  /** 消费一个完整行（不含行终止符） */
  function handleLine(line: string): void {
    // 空行 = 事件块结束
    if (line === '') {
      dispatchEventBlock();
      return;
    }

    // 注释行（`:keep-alive` 心跳）。不置 sawField：
    // 纯心跳块不构成事件，不应触发空 data 的派发
    if (line.startsWith(':')) return;

    const colon = line.indexOf(':');
    // 规范：无冒号的行视为"字段名 + 空值"，未知字段一律忽略
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    // 规范：冒号后紧跟的**一个**空格是分隔符，不属于值（0 个或 1 个都合法）
    if (value.startsWith(' ')) value = value.slice(1);

    sawField = true;

    if (field === 'event') {
      eventName = value;
      return;
    }
    if (field === 'data') {
      if (dataStartOffset === -1) dataStartOffset = streamOffset;
      dataLines.push(value);
      return;
    }
    // id / retry 等字段本项目不使用，静默忽略
  }

  /**
   * 报错并复位当前块。
   * 一次性清空 dataLines 是刻意的：坏帧之后若还留着上一帧的 data，
   * 下一个合法块会拼出"半旧半新"的畸形 JSON，产生难以定位的二次故障。
   */
  function fail(raw: string, event: string, reason: string): void {
    resetEventBlock();
    onParseError?.({ raw, event, reason });
  }

  /** 派发当前累积的事件块 */
  function dispatchEventBlock(): void {
    if (!sawField || dataLines.length === 0) {
      resetEventBlock();
      return;
    }

    const raw = dataLines.join('\n');
    const resolvedEvent = eventName ?? '';
    resetEventBlock();

    // [DONE] 哨兵：契约 §3.3 的流用 done 事件收尾，不会出现哨兵；
    // 但上游 OpenAI 兼容流会，统一在解析层丢弃，避免业务到处写 if
    if (raw === SSE_DONE_SENTINEL) return;

    // 事件名必须是契约声明的 5 个之一；否则整帧无意义，按解析错误上报
    if (!isSseEventName(resolvedEvent)) {
      fail(raw, resolvedEvent, `未知的 SSE 事件名: ${resolvedEvent === '' ? '(缺失)' : resolvedEvent}`);
      return;
    }

    let json: unknown;
    try {
      json = JSON.parse(raw);
    } catch (error) {
      fail(raw, resolvedEvent, error instanceof Error ? error.message : String(error));
      return;
    }

    // payload 结构化校验：字段缺失/类型错误都在这里被拦下，
    // 而不是让下游在渲染时炸出一个 undefined.content。
    // 先断言事件名再索引 schema：联合类型索引会让 TS 把 safeParse 的结果也并成联合，
    // 从而拿不到成功分支上的 data
    if (!isSseEventName(resolvedEvent)) {
      fail(raw, resolvedEvent, `未知的 SSE 事件名: ${resolvedEvent === '' ? '(缺失)' : resolvedEvent}`);
      return;
    }
    const parsed = SSE_PAYLOAD_SCHEMAS[resolvedEvent].safeParse(json);
    if (!parsed.success) {
      fail(raw, resolvedEvent, summarizeIssues(parsed.error));
      return;
    }

    // 这里需要一次断言：SseEventName 是联合类型，`SseEvent<SseEventName>` 的
    // data 是各 payload 的联合，而 SseEvent<K> 的 data 是单一类型。
    // 上面的 safeParse 已经保证 parsed.data 与 resolvedEvent 对应，
    // 但 TS 无法跨索引访问保留这种关联（分布式对象类型不可表达）。
    const evt = {
      event: resolvedEvent,
      data: parsed.data,
      raw,
    } as SseEvent<SseEventName>;

    onEvent(evt);
  }

  /** 把 buffer 里所有完整行消费掉 */
  function drainLines(): void {
    for (;;) {
      const lf = buffer.indexOf('\n');
      const cr = buffer.indexOf('\r');

      let terminator = -1;
      if (lf !== -1 && (cr === -1 || lf < cr)) {
        terminator = lf;
      } else if (cr !== -1) {
        terminator = cr;
      }

      // 没有行终止符：剩下的都是半包，留在 buffer 等下一个 chunk
      if (terminator === -1) return;

      // buffer 以 \r 结尾且下一字符尚未到达：无法判定是 CRLF 还是裸 CR。
      // 若此刻就消费，CRLF 会被误判成"空行 + 新行"，凭空多切一个事件块。
      if (buffer.charCodeAt(terminator) === 0x0d && terminator + 1 >= buffer.length) return;

      const line = buffer.slice(0, terminator);
      // CRLF 要把 \n 一起吃；裸 CR 只消费一个字符
      const consume = buffer.charCodeAt(terminator) === 0x0d && buffer.charCodeAt(terminator + 1) === 0x0a
        ? terminator + 2
        : terminator + 1;

      buffer = buffer.slice(consume);
      streamOffset += consume;

      handleLine(line);
    }
  }

  return {
    push(chunk: string): void {
      if (chunk.length === 0) return;

      let input = chunk;
      // BOM 只可能出现在流首；不剥掉会让第一行变成 "\uFEFFevent: meta" 从而丢失首帧
      if (!bomStripped) {
        bomStripped = true;
        if (input.charCodeAt(0) === 0xfeff) input = input.slice(1);
      }

      buffer += input;
      drainLines();
    },

    flush(): void {
      // 流末尾可能没有换行：把残余当作最后一行处理
      if (buffer.length > 0) {
        let line = buffer;
        buffer = '';
        if (line.endsWith('\r')) line = line.slice(0, -1);
        streamOffset += line.length;
        // 空行在流末尾等价于"块已结束"，交给下面的 dispatchEventBlock 统一处理；
        // 这里若也调 handleLine('') 会在块已被空行收尾时二次派发（重复事件）
        if (line !== '') handleLine(line);
      }

      // 流末尾可能没有空行收尾：显式派发最后一个事件块
      dispatchEventBlock();
      resetEventBlock();
    },

    reset(): void {
      buffer = '';
      streamOffset = 0;
      resetEventBlock();
      bomStripped = false;
    },
  };
}

/** zod issues → 一行可读原因（只取前 3 条，避免日志被长表单刷屏） */
function summarizeIssues(error: unknown): string {
  if (typeof error !== 'object' || error === null || !('issues' in error)) {
    return String(error);
  }
  const issues = (error as { issues: unknown }).issues;
  if (!Array.isArray(issues)) return String(error);

  return issues
    .slice(0, 3)
    .map((issue: unknown) => {
      if (typeof issue !== 'object' || issue === null) return String(issue);
      const path = 'path' in issue && Array.isArray((issue as { path: unknown }).path)
        ? ((issue as { path: unknown[] }).path as unknown[]).map(String).join('.')
        : '';
      const message = 'message' in issue ? String((issue as { message: unknown }).message) : 'invalid';
      return path === '' ? message : `${path}: ${message}`;
    })
    .join('; ');
}

/* ------------------------------------------------------------------ *
 * 便捷封装
 * ------------------------------------------------------------------ */

/** 用 payload schema 校验一次事件；失败返回 null 而不是抛异常 */
export function validateSsePayload<K extends SseEventName>(
  event: K,
  payload: unknown,
): SseEventMap[K] | null {
  const parsed = SSE_PAYLOAD_SCHEMAS[event].safeParse(payload);
  return parsed.success ? (parsed.data as SseEventMap[K]) : null;
}

/**
 * 服务端封帧（Lead Directives §5.1）：`event: <name>\n` + `data: <json>\n\n`。
 * 两个 `\n` 结束帧——少一个会让客户端一直等下一个字段，表现为"回答卡住不结束"。
 */
export function formatSseFrame(event: SseEventName, payload: unknown): string {
  return `event: ${event}\ndata: ${JSON.stringify(payload)}\n\n`;
}

/** SSE 响应头（契约 §3.3 + Lead Directives §5.1） */
export function sseResponseHeaders(): Record<string, string> {
  return {
    'Content-Type': 'text/event-stream; charset=utf-8',
    'Cache-Control': 'no-cache, no-transform',
    Connection: 'keep-alive',
    // Nginx 默认会缓冲响应导致流式"憋住"，必须显式关闭
    'X-Accel-Buffering': 'no',
  };
}

/** 心跳帧：用于长思考期间保活，避免中间代理空闲超时断连 */
export const SSE_HEARTBEAT_FRAME = ': keep-alive\n\n';

/**
 * 把增量 delta 序列拼成完整文本。解析器不做聚合，这是两端共用的聚合实现。
 * 按 `index` 排序后再拼接：代理层理论上保序，但并发写响应时出现乱序的代价
 * 是"用户看到错乱的句子"，比多一次 sort 昂贵得多。
 */
export function joinDeltaContents(chunks: readonly { index: number; content: string }[]): string {
  return [...chunks]
    .sort((a, b) => a.index - b.index)
    .map((chunk) => chunk.content)
    .join('');
}

/**
 * 面向 `fetch`/`ReadableStream` 消费方的高层封装。
 *
 * 只接受 `AsyncIterable<string>`（调用方负责 TextDecoder 与
 * `{ stream: true }` 的跨 chunk 多字节字符拼接），
 * 因为小程序端的 chunk 是 ArrayBuffer，解码方式与 Web 不同；
 * 把解码留在调用侧，解析器才能对两端完全一致。
 */
export async function consumeSseStream(
  source: AsyncIterable<string>,
  options: SseParserOptions,
): Promise<{ events: number; parseErrors: number }> {
  let events = 0;
  let parseErrors = 0;
  // 计数器必须始终挂上：调用方没传 onParseError 时同样要统计坏帧数，
  // 否则"未传回调"会表现为"零解析错误"，掩盖真实的流损坏
  const parser = createSseParser({
    onEvent: (evt) => {
      events += 1;
      options.onEvent(evt);
    },
    onParseError: (err: SseParseErrorInfo) => {
      parseErrors += 1;
      options.onParseError?.(err);
    },
  });

  for await (const chunk of source) {
    parser.push(chunk);
  }
  parser.flush();

  return { events, parseErrors };
}

/**
 * 把一个已经完整读取的字符串一次性解析（测试、日志回放、契约冒烟脚本用）。
 * 生产流式路径不要用它——会先攒满整个响应再渲染。
 */
export function parseSseString(
  text: string,
  options: SseParserOptions,
): { events: number; parseErrors: number } {
  let events = 0;
  let parseErrors = 0;
  const parser = createSseParser({
    onEvent: (evt) => {
      events += 1;
      options.onEvent(evt);
    },
    onParseError: (err) => {
      parseErrors += 1;
      options.onParseError?.(err);
    },
  });

  parser.push(text);
  parser.flush();
  return { events, parseErrors };
}
